import { useBunja } from 'bunja/react';
import { ReactNode, useMemo } from 'react';
import { useMount } from 'react-use';

import { getFillHistoriesApi } from '@flipster/apis/v2/history';
import { fillHistoryInstance } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Chip } from '@flipster/react/design-system/chip';
import CalendarDaysIcon from '@flipster/react/icon/generated/CalendarDaysIcon';
import { Col } from '@flipster/ui/Table';

import { useAuth } from '../../../shared/auth/state';
import { DatePicker } from '../../../shared/date-picker';
import { useStepPagination } from '../../../shared/pagination/use-step-pagination';
import { historyExportState } from '../../state/export';
import ActionButtons from '../action-buttons';
import { filterSection, left } from '../styles';
import SymbolSelector from '../symbol-selector';
import { TableList } from '../table-list';
import { FillDesktopRow } from './fill-desktop-row';
import { FillMobileCard } from './fill-mobile-card';
import { selectors } from './styles';

interface Props {
  fillOrderSelectBox: ReactNode;
  productType: ProductType;
}

export const getFillHistoryColumns = (productType: ProductType): Col[] => [
  { title: <FormattedMessage id='Common.r250401.Date' />, width: 140, key: 'date' },
  { title: <FormattedMessage id='Assets.r250401.Symbol' />, width: 170, key: 'symbol' },
  {
    title:
      productType === ProductType.Perpetual ? (
        <FormattedMessage id='Assets.r250401.Side' />
      ) : (
        <FormattedMessage id='Autogen_common.r251126.s_Buy_slash_sell' />
      ),
    width: 70,
    key: 'side',
  },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Type' />, width: 100, key: 'type' },
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Fill_price' />,
    align: 'right',
    width: 130,
    key: 'fillPrice',
  },
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Order_price' />,
    align: 'right',
    width: 130,
    key: 'orderPrice',
  },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Fill_qty' />, align: 'right', width: 140, key: 'qty' },
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Fee_Type_slash_Fee' />,
    align: 'right',
    width: 160,
    key: 'fee',
  },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Value' />, align: 'right', width: 100, key: 'value' },
];

export const FillHistory = ({ fillOrderSelectBox, productType }: Props) => {
  const { isCookieLoggedIn } = useAuth();
  const router = useRouter();
  const { setExportParams } = useBunja(historyExportState);

  const {
    isLoading,
    params,
    data,
    hasPrev,
    hasNext,
    setRangeFilter,
    setSymbolFilter,
    setPagination,
    resetParams,
    refetch,
  } = useStepPagination('history', ['getFillHistoriesApi'], getFillHistoriesApi, {
    enabled: isCookieLoggedIn,
    passParams: {
      productType,
    },
  });

  const range = useMemo(
    () => (params?.startTime && params?.endTime ? { startDate: params.startTime, endDate: params.endTime } : undefined),
    [params],
  );

  useMount(() => {
    resetParams();
  });

  const handleClickExport = () => {
    setExportParams({
      category: productType === ProductType.Perpetual ? 'perpetual' : 'spot',
      subCategory: 'trade',
      siblingCategory: 'fill',
      additionalOption: {
        symbol: params?.symbol,
      },
    });
    router.push('/history/export');
  };
  return (
    <>
      <section className={filterSection}>
        <div className={left}>
          <DatePicker.Root>
            <DatePicker.Trigger asChild>
              <Chip.Choice
                type='square'
                icon={<CalendarDaysIcon />}
                selected={Boolean(params?.startTime) && Boolean(params?.endTime)}
              />
            </DatePicker.Trigger>
            <DatePicker.RangePicker
              portal
              selected={range}
              onChange={(range) => {
                setRangeFilter([range?.startDate, range?.endDate]);
              }}
              confirmText={<FormattedMessage id='Common.r250923.Search' />}
            />
          </DatePicker.Root>
          <div className={selectors}>
            {fillOrderSelectBox}
            <SymbolSelector symbol={params?.symbol} onSelect={setSymbolFilter} productType={productType} />
          </div>
        </div>
        <ActionButtons refetch={refetch} onClickExport={handleClickExport} />
      </section>

      <TableList
        isLoading={isLoading}
        items={data?.map(fillHistoryInstance)}
        renderCardItem={(item) => <FillMobileCard item={item} key={`card-${item.execId}`} productType={productType} />}
        renderTableItem={(item) => <FillDesktopRow item={item} key={`row-${item.execId}`} productType={productType} />}
        columns={getFillHistoryColumns(productType)}
        pagination={{ cursorKey: 'execId', hasPrev, hasNext, onChange: setPagination }}
      />
    </>
  );
};
